import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Stack,
  Paper,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Import images
import DataScienceImg from "../../images/DataScience.jpg";
import OfficeImg from "../../images/office.jpg";
import TelecomImg from "../../images/telecom.jpg";
import TalkImg1 from "../../images/talk1.png";
import TalkImg2 from "../../images/talk2.png";
import TalkImg3 from "../../images/talk3.png";
import TalkImg4 from "../../images/talk4.png";

const OurServices = () => {
  // Hero Section
  const HeroSection = () => (
    <Box
      sx={{
        background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
        color: "white",
        py: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2rem", md: "2.4rem" },
          }}
        >
          Our Services
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            opacity: 0.95,
            fontSize: { xs: "0.95rem", md: "1.1rem" },
          }}
        >
          Comprehensive staffing and project solutions tailored to your business
          needs
        </Typography>
      </Container>
    </Box>
  );

  // Featured Services with Images Section
  const FeaturedServicesSection = () => {
    const services = [
      {
        id: 1,
        title: "STAFFING & RECRUITMENT",
        image: TalkImg1,
        color: "#2196F3",
      },
      {
        id: 2,
        title: "WORKFORCE SOLUTIONS",
        image: TalkImg2,
        color: "#2196F3",
      },
      {
        id: 3,
        title: "EXECUTIVE SERVICES",
        image: TalkImg3,
        color: "#2196F3",
      },
      {
        id: 4,
        title: "TECHNOLOGIES SOLUTIONS",
        image: TalkImg4,
        color: "#2196F3",
      },
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {services.map((service) => (
              <Grid item xs={12} sm={6} md={3} key={service.id}>
                <Card
                  sx={{
                    height: "400px",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                      transform: "translateY(-8px)",
                    },
                    "&:hover .service-overlay": {
                      opacity: 1,
                    },
                  }}
                >
                  {/* Image */}
                  <CardMedia
                    component="img"
                    image={service.image}
                    alt={service.title}
                    sx={{
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  {/* Overlay */}
                  <Box
                    className="service-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(33, 150, 243, 0.7) 0%, rgba(33, 150, 243, 0.5) 100%)",
                      display: "flex",
                      alignItems: "flex-end",
                      padding: "20px",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      zIndex: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "white",
                        textAlign: "center",
                        width: "100%",
                        fontSize: { xs: "0.9rem", md: "22px" },
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      {service.title}
                    </Typography>
                  </Box>

                  {/* Label at Bottom */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "rgba(0, 0, 0, 0.3)",
                      padding: "16px",
                      display: "flex",
                      justifyContent: "center",
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#2196F3",
                        textAlign: "center",
                        fontSize: { xs: "0.85rem", md: "22px" },
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {service.title}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Staffing & Recruitment Categories Section
  const StaffingCategoriesSection = () => {
    const categories = [
      "Accounting & Finance",
      "Call Center",
      "Engineering",
      "Healthcare",
      "Human Resources",
      "Industrial",
      "Legal",
      "Life Sciences",
      "Marketing",
      "Non-Clinical Healthcare",
      "Office & Administration",
      "Technologies",
    ];

    return (
      <Box sx={{ py: { xs: 4, md: 4 }, bgcolor: "#f5f7fa" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: "#1e3c72",
              fontSize: { xs: "1.8rem", md: "32px" },
            }}
          >
            Staffing & Recruitment
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "#666",
              fontWeight: "600",
              fontSize: { xs: "0.95rem", md: "15px" },
              lineHeight: 1.6,
            }}
          >
            Your organization's culture and goals are just as important during
            the recruiting process as finding an experienced professional. Our
            recruiters are skilled at finding those perfect-match employees in
            the following areas:
          </Typography>

          <Grid container spacing={2}>
            {categories.map((category, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  sx={{
                    py: { xs: 1.5, md: 2 },
                    px: { xs: 2, md: 3 },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      bgcolor: "#fafbfc",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#000",
                      fontSize: { xs: "0.95rem", md: "22px" },
                      textTransform: "capitalize",
                    }}
                  >
                    {category}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#2a5298",
                      fontSize: "1.2rem",
                    }}
                  >
                    &gt;
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Executive Services Section
  const ExecutiveServicesSection = () => {
    const services = [
      "Executive Search",
      "Interim Executive Services",
      "Project Management",
      "Strategic Business Solutions",
    ];

    return (
      <Box sx={{ bgcolor: "#f5f7fa" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: "#1e3c72",
              paddingTop: "20px",
              fontSize: { xs: "1.8rem", md: "32px" },
            }}
          >
            Executive Services
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "#666",
              fontWeight: "600",
              fontSize: { xs: "0.95rem", md: "15px" },
              lineHeight: 1.6,
            }}
          >
            Transform, stabilize and optimize your organization with C-suite
            interim leadership, consulting and search services. Here are some of
            the ways we can help you meet your goals.
          </Typography>

          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  sx={{
                    py: { xs: 1.5, md: 2 },
                    px: { xs: 2, md: 3 },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#f9f9f9",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      bgcolor: "#fafbfc",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#000",
                      fontSize: { xs: "0.95rem", md: "22px" },
                      textTransform: "capitalize",
                    }}
                  >
                    {service}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#2a5298",
                      fontSize: "1.2rem",
                    }}
                  >
                    &gt;
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Technology Solutions Section
  const TechnologySolutionsSection = () => {
    const services = [
      "Application Services",
      "End-User Services",
      "Engineering Solutions",
      "Infrastructure Services",
      "PMO Services",
      "Strategy and Architecture",
      "Technology Staffing",
    ];

    return (
      <Box sx={{ py: { xs: 4, md: 4 }, bgcolor: "#f5f7fa" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"

            sx={{
              fontWeight: 700,
              mb: 2,
              marginTop: "20px",
              color: "#1e3c72",
              fontSize: { xs: "1.8rem", md: "32px" },
            }}
          >
            Technology Solutions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "#666",
              fontWeight: "600",
              fontSize: { xs: "0.95rem", md: "15px" },
              lineHeight: 1.6,
            }}
          >
            Find skilled tech talent matching the exact qualifications your team
            demands, implement a new application and keep up with the latest
            technologies thanks to our suite of technology solutions. Learn more
            about our full lineup of technologies offerings below.
          </Typography>

          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  sx={{
                    py: { xs: 1.5, md: 2 },
                    px: { xs: 2, md: 3 },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#ffffff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      bgcolor: "#fafbfc",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#000",
                      fontSize: { xs: "0.95rem", md: "22px" },
                      textTransform: "capitalize",
                    }}
                  >
                    {service}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#2a5298",
                      fontSize: "1.2rem",
                    }}
                  >
                    &gt;
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  return (
    <Box>
      <HeroSection />
      <FeaturedServicesSection />
      <StaffingCategoriesSection />
      <ExecutiveServicesSection />
      <TechnologySolutionsSection />
    </Box>
  );
};

export default OurServices;
